import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useUser } from "../context/UserContext";
import LoadingScreen from "./LoadingScreen";
import { clearAuthToken, getAuthToken } from "@/lib/authStorage";
import { getUserData } from "@/api/userService";
import { useToast } from "@chakra-ui/react";

const AuthWrapper = ({
  children,
  accessRules = {
    admin: true,
    subscriber: true,
    authenticatedRoutes: ["/gastos", "/ingresos", "/deducciones", "/administracion", "/perfiles", "/datos"],
  },
}) => {
  const { user, setUserData } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const toast = useToast();

  const initialize = async () => {
    const token = getAuthToken();
    const { pathname } = router;

    const isLoginPage = pathname === "/login";
    const isIndexPage = pathname === "/";

    const isAdminRoute = accessRules.admin && pathname.startsWith("/admin");
    const isSubscriptionRoute =
      accessRules.subscriber && pathname.startsWith("/suscripcion");
    const isProtectedRoute =
      accessRules.authenticatedRoutes &&
      accessRules.authenticatedRoutes.includes(pathname);

    const handleRedirect = (to, title, description, status = "info") => {
      toast({
        title,
        description,
        status,
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      router.push(to);
    };

    if (token) {
      try {
        let userData = user;

        if (!user) {
          userData = await getUserData(token);
          setUserData(userData);
        }

        if (isLoginPage) {
          return router.push("/");
        }

        if (isAdminRoute && !userData.isAdmin) {
          return handleRedirect(
            "/",
            "Acceso denegado",
            "No tienes permisos para acceder a esta sección.",
            "error"
          );
        }

        if (isSubscriptionRoute && userData?.subscriptionStatus.isSubscriber) {
          return handleRedirect(
            "/",
            "Suscripción requerida",
            "Debes tener una suscripción activa para acceder."
          );
        }

        if (isProtectedRoute && !userData) {
          return router.push("/login");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        clearAuthToken();

        if (!isLoginPage && !isIndexPage) {
          return handleRedirect(
            "/login",
            "Sesión inválida",
            "Tu sesión ha expirado o es inválida. Inicia sesión nuevamente.",
            "warning"
          );
        }
      }
    } else {
      if (
        !isLoginPage &&
        !isIndexPage &&
        (isAdminRoute || isSubscriptionRoute || isProtectedRoute)
      ) {
        return handleRedirect(
          "/login",
          "Inicio de sesión requerido",
          "Necesitas iniciar sesión para acceder a esta sección."
        );
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    if (!user) {
      initialize();
    } else {
      setIsLoading(false);
    }
  }, [router.pathname, user]);

  return (
    <>
      <LoadingScreen isLoading={isLoading} />
      {!isLoading && children}
    </>
  );
};

export default AuthWrapper;
